import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useUserSetting } from '../../../hooks/use-user-setting';
import { saveUserSetting } from '../../../store/user/user.actions';
import { TIME_PERIOD } from '../utils/time-periods';

/**
 * Constants for forecasting settings keys to avoid magic strings
 */
export const FORECASTING_SETTING_KEYS = {
  FORECAST_PERIOD: 'forecastPeriod',
  LOOKBACK_PERIOD: 'lookbackPeriod',
  SALES_TREND: 'salesTrend',
  LEAD_TIME_DAYS: 'leadTimeDays',
  BUFFER_STOCK_DAYS: 'bufferStockDays',
};

export const DEFAULT_SETTINGS = {
  [FORECASTING_SETTING_KEYS.FORECAST_PERIOD]: TIME_PERIOD.DAYS_60,
  [FORECASTING_SETTING_KEYS.LOOKBACK_PERIOD]: TIME_PERIOD.YEAR_1,
  [FORECASTING_SETTING_KEYS.SALES_TREND]: 0,
  [FORECASTING_SETTING_KEYS.LEAD_TIME_DAYS]: 5,
  [FORECASTING_SETTING_KEYS.BUFFER_STOCK_DAYS]: 3,
};

const ForecastingSettingsContext = createContext();

export const ForecastingSettingsProvider = ({ children }) => {
  const dispatch = useDispatch();
  const forecastConfigsJson = useUserSetting(UserSettings.inventorySmartReorderForecastConfigs?.settingName);

  const settings = useMemo(() => {
    let parsedSettings = DEFAULT_SETTINGS;

    if (forecastConfigsJson) {
      try {
        const parsed = JSON.parse(forecastConfigsJson);
        parsedSettings = { ...parsedSettings, ...parsed };
      } catch {
        // If JSON parsing fails, use default settings
        parsedSettings = DEFAULT_SETTINGS;
      }
    }

    return parsedSettings;
  }, [forecastConfigsJson]);

  const applySettings = useCallback(
    (newSettings) => {
      const mergedSettings = { ...settings, ...newSettings };
      const settingsJson = JSON.stringify(mergedSettings);

      dispatch(
        saveUserSetting({
          settingName: UserSettings.inventorySmartReorderForecastConfigs.settingName,
          settingValue: settingsJson,
          excludeV3prefix: true,
        }),
      );
    },
    [dispatch, settings],
  );

  const resetSettings = useCallback(() => {
    const settingsJson = JSON.stringify(DEFAULT_SETTINGS);

    dispatch(
      saveUserSetting({
        settingName: UserSettings.inventorySmartReorderForecastConfigs.settingName,
        settingValue: settingsJson,
        excludeV3prefix: true,
      }),
    );

    return DEFAULT_SETTINGS;
  }, [dispatch]);

  const contextValue = useMemo(
    () => ({
      settings,
      applySettings,
      resetSettings,
    }),
    [settings, applySettings, resetSettings],
  );

  return <ForecastingSettingsContext.Provider value={contextValue}>{children}</ForecastingSettingsContext.Provider>;
};

export const useForecastingSettings = () => {
  return useContext(ForecastingSettingsContext);
};
